import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Search, RefreshDouble } from 'iconoir-react';

interface ActivityLog {
  id: number;
  user: { id: number; name: string; email: string } | null;
  action: string;
  description: string | null;
  created_at: string;
  ip_address: string | null;
}

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/activity-logs', { params: filters });
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadLogs();
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      login: '🔐 Connexion',
      logout: '🚪 Déconnexion',
      board_created: '📋 Tableau créé',
      board_updated: '✏️ Tableau modifié',
      board_deleted: '🗑️ Tableau supprimé',
      card_created: '📝 Carte créée',
      card_updated: '✏️ Carte modifiée',
      card_deleted: '🗑️ Carte supprimée',
      user_invited: '👥 Utilisateur invité',
    };
    return labels[action] || action;
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="activity-logs">
      <div className="section-header">
        <h2>Logs d'activité</h2>
        <button className="btn btn-secondary" onClick={loadLogs}>
          <RefreshDouble width={18} height={18} strokeWidth={2} />
          Actualiser
        </button>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Action</label>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="form-control"
          >
            <option value="">Toutes</option>
            <option value="login">Connexion</option>
            <option value="logout">Déconnexion</option>
            <option value="board_created">Tableau créé</option>
            <option value="card_created">Carte créée</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Date début</label>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="form-control"
          />
        </div>
        <div className="filter-group">
          <label>Date fin</label>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="form-control"
          />
        </div>
        <button className="btn btn-primary" onClick={handleSearch}>
          <Search width={18} height={18} strokeWidth={2} />
          Rechercher
        </button>
      </div>

      <div className="logs-list">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date/Heure</th>
              <th>Utilisateur</th>
              <th>Action</th>
              <th>Description</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td>
                  {new Date(log.created_at).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
                <td>
                  {log.user ? (
                    <div>
                      <strong>{log.user.name}</strong>
                      <br />
                      <small>{log.user.email}</small>
                    </div>
                  ) : (
                    <span className="text-muted">Système</span>
                  )}
                </td>
                <td>{getActionLabel(log.action)}</td>
                <td>{log.description || '-'}</td>
                <td>
                  <code>{log.ip_address || '-'}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityLogs;
